import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Environment, ENVIRONMENT_DI } from '../../environment/environment';
import { SettingsService } from '../../shared/services/admin-settings.service';
import { IdentityService } from '../../core/identity/identity.service';
import { SiteRoleService } from '../../core/identity/site-role.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { AccountAdminService } from 'app/back-office/account-admin.service';
import { BugreportComponent } from 'app/back-office/bugreport/bugreport.component';
import { SiteContextService, SiteSummary } from '../../core/site-context.service';
import { CustomersService } from 'app/customers/customers.service';
import { TopLevelSideMenuService } from '../top-level-side-menu/top-level-side-menu.service';
import {
  FeatureFlagState,
  SubNavAction,
  SubNavEntry,
  SubNavGroup,
  SubNavItem,
  TOP_LEVEL_NAV,
  TopLevelNavId,
  TopLevelNavItem,
  isSubNavGroup,
  isSubNavItemExpanded,
  isSubNavSeparator,
  resolveSubNavEntries,
} from '../navigation.config';
import { AppExperiencesService } from 'app/shared/services/app-experiences.service';

interface MobileNavLeafVm {
  kind: 'link' | 'external' | 'action' | 'disabled';
  translationKey: string;
  url: string | null;
  externalUrl: string | null;
  queryParams: Record<string, string> | null;
  actionType: SubNavAction | null;
  dataTest: string | null;
  betaTag: boolean;
  adminOnly: boolean;
  showUpgradeTag: boolean;
  children: MobileNavLeafVm[];
  showChildren: boolean;
}

interface MobileNavGroupVm {
  kind: 'group';
  translationKey: string;
  dataTest: string | null;
  adminOnly: boolean;
  expanded: boolean;
  items: MobileNavLeafVm[];
}

interface MobileNavSeparatorVm {
  kind: 'separator';
}

type MobileNavEntryVm = MobileNavLeafVm | MobileNavGroupVm | MobileNavSeparatorVm;

interface MobileNavAccountVm {
  id: number;
  name: string;
}

interface MobileNavSectionVm {
  id: TopLevelNavId;
  translationKey: string;
  icon: string;
  url: string;
  dataTest: string;
  newTag: boolean;
  adminOnly: boolean;
  showUpgradeTag: boolean;
  active: boolean;
  expanded: boolean;
  hasEntries: boolean;
  togglesOnly: boolean;
  entries: MobileNavEntryVm[];
}

@Component({
  selector: 'app-mobile-nav',
  templateUrl: './mobile-nav.component.html',
  styleUrls: ['./mobile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MobileNavComponent {
  private readonly menuService = inject(TopLevelSideMenuService);
  private readonly settingsService = inject(SettingsService);
  private readonly appExperiencesService = inject(AppExperiencesService);
  private readonly identityService = inject(IdentityService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly accountAdminService = inject(AccountAdminService);
  private readonly siteContext = inject(SiteContextService);
  private readonly customersService = inject(CustomersService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly environment = inject<Environment>(ENVIRONMENT_DI);

  protected readonly mobileOpen = this.menuService.mobileOpen;

  private readonly settings = toSignal(this.settingsService.settings$, {
    initialValue: this.settingsService.getCurrentSettings(),
  });

  // Tier access drives the "upgrade" tags (e.g. on the activity log). Loads asynchronously with
  // the account context, so it lives in a signal to keep the section view model reactive.
  private readonly tierAccess = toSignal(
    this.currentContextService.currentContextBS.pipe(map((context) => context?.tierAccess ?? null)),
    { initialValue: this.currentContextService.currentContext?.tierAccess ?? null },
  );

  private readonly earlyAccessFeatures = toSignal(
    this.currentContextService.currentContextBS.pipe(map((context) => context?.user?.earlyAccessFeatures ?? [])),
    { initialValue: this.currentContextService.currentContext?.user?.earlyAccessFeatures ?? [] },
  );

  // Identity is stable for the lifetime of the authenticated shell that hosts this component.
  private readonly identity = this.identityService.currentIdentity;
  private readonly isAdmin = this.identity?.isAdmin() ?? false;
  private readonly isSiteAdmin = inject(SiteRoleService).isAccountAdmin;

  // --- Account switcher (footer) ---
  // The current account name, kept reactive off the shared context so it tracks switches.
  protected readonly currentAccountName = toSignal(
    this.currentContextService.currentContextBS.pipe(map((context) => context?.account?.name ?? null)),
    { initialValue: this.currentContextService.currentContext?.account?.name ?? null },
  );
  // Only offer switching when the user actually belongs to more than one account.
  protected readonly hasMultipleAccounts = (this.identity?.getAllSiteIds()?.length ?? 0) > 1;

  protected readonly accountSwitcherOpen = signal(false);
  protected readonly accountsLoading = signal(false);
  protected readonly switchingAccount = signal(false);
  private readonly accounts = signal<MobileNavAccountVm[]>([]);
  private readonly accountFilter = signal('');

  protected readonly filteredAccounts = computed<MobileNavAccountVm[]>(() => {
    const query = this.accountFilter().trim().toLowerCase();
    const accounts = this.accounts();
    if (!query) {
      return accounts;
    }
    return accounts.filter((a) => a.name.toLowerCase().includes(query));
  });
  // A filter box only earns its place once the list is long enough to need it.
  protected readonly showAccountFilter = computed(() => this.accounts().length > 7);

  // Tracks which top-level sections / groups the user has explicitly toggled.
  private readonly sectionState = signal<Map<TopLevelNavId, boolean>>(new Map());
  private readonly groupState = signal<Map<string, boolean>>(new Map());

  protected readonly routerLinkActiveOptions = {
    paths: 'exact' as const,
    queryParams: 'subset' as const,
    matrixParams: 'ignored' as const,
    fragment: 'ignored' as const,
  };

  private readonly isProduction = this.environment.isProduction;

  protected readonly sections = computed<MobileNavSectionVm[]>(() => {
    const settings = this.settings();
    const currentUrl = this.menuService.currentUrl();
    const activeId = this.menuService.activeTopLevel()?.id;
    const sectionState = this.sectionState();
    const groupState = this.groupState();
    const path = currentUrl.split('?')[0];
    const backoffice = path.startsWith('/backoffice');
    const adminToggle = settings?.adminToggle ?? false;
    const featureFlags: FeatureFlagState = {
      appExperiences: this.appExperiencesService.enabled(),
    };

    return TOP_LEVEL_NAV.filter((item) => this.isTopLevelVisible(item, { backoffice, adminToggle, featureFlags })).map(
      (item) => {
        const resolved = resolveSubNavEntries(item, {
          isProduction: this.isProduction,
          earlyAccessFeatures: this.earlyAccessFeatures(),
          adminToggle,
        });
        const entries = this.buildEntries(resolved, path, groupState);
        const active = item.id === activeId;
        const explicitExpanded = sectionState.get(item.id);
        return {
          id: item.id,
          translationKey: item.translationKey,
          icon: item.icon,
          url: item.url,
          dataTest: item.dataTest,
          newTag: !!item.newTag,
          adminOnly: !!item.adminOnly,
          showUpgradeTag: this.shouldShowUpgradeTagForTopLevel(item),
          active,
          // Default-expand the active section; otherwise respect explicit toggles.
          expanded: explicitExpanded ?? active,
          hasEntries: entries.length > 0,
          togglesOnly: !!item.mobileToggleOnly,
          entries,
        };
      },
    );
  });

  protected toggleSection(section: MobileNavSectionVm): void {
    if (section.togglesOnly) {
      this.setSectionExpanded(section.id, !section.expanded);
      return;
    }
    // First tap expands; a second tap, while already expanded, navigates to the section page.
    if (section.expanded) {
      this.router.navigate([section.url]);
      this.menuService.closeMobile();
      return;
    }
    this.setSectionExpanded(section.id, true);
  }

  protected toggleSectionChevron(event: Event, section: MobileNavSectionVm): void {
    event.stopPropagation();
    this.setSectionExpanded(section.id, !section.expanded);
  }

  private setSectionExpanded(id: TopLevelNavId, expanded: boolean): void {
    this.sectionState.update((map) => {
      const next = new Map(map);
      next.set(id, expanded);
      return next;
    });
  }

  protected toggleGroup(group: MobileNavGroupVm): void {
    this.groupState.update((map) => {
      const next = new Map(map);
      next.set(group.translationKey, !group.expanded);
      return next;
    });
  }

  protected closeOverlay(): void {
    this.menuService.closeMobile();
  }

  // Leaf navigation closes the overlay. (Router navigation also clears it, but closing here
  // covers same-route taps and feels instant.)
  protected onNavigate(): void {
    this.menuService.closeMobile();
  }

  // Toggles the account list, loading the user's other accounts on first open.
  protected toggleAccountSwitcher(): void {
    if (!this.hasMultipleAccounts) {
      return;
    }
    const next = !this.accountSwitcherOpen();
    this.accountSwitcherOpen.set(next);
    if (next && this.accounts().length === 0 && !this.accountsLoading()) {
      this.loadAccounts();
    }
  }

  protected onAccountFilter(value: string): void {
    this.accountFilter.set(value);
  }

  // Switches the active account. SiteContextService reloads the app, so there's no need to
  // reset local state afterwards; the guard just blocks double taps during the switch.
  protected async switchAccount(siteId: number): Promise<void> {
    if (this.switchingAccount()) {
      return;
    }
    this.switchingAccount.set(true);
    // Clear the cached customer first so the new account's customer context is reloaded,
    // mirroring the desktop site selector.
    this.customersService.clearCurrentCustomerCache();
    try {
      await this.siteContext.setCurrentSiteAsync(siteId);
    } catch {
      // The reload failed to kick in; let the user try again.
      this.switchingAccount.set(false);
    }
  }

  private loadAccounts(): void {
    this.accountsLoading.set(true);
    this.siteContext
      .getAllSitesExceptCurrentAsync()
      .then((sites: SiteSummary[]) => {
        this.accounts.set(sites.map((s) => ({ id: s.id, name: s.name })));
      })
      .catch(() => {
        this.accounts.set([]);
      })
      .finally(() => {
        this.accountsLoading.set(false);
      });
  }

  // Non-route menu items (sign out, admin dialogs, backoffice). Mirrors the desktop mega menu.
  protected onAction(actionType: SubNavAction | null): void {
    switch (actionType) {
      case 'sign-out':
        this.identityService.signOut('user-initiated-mobile-nav');
        return;
      case 'open-bugreport':
        this.openBugReport();
        break;
      case 'open-admin-settings':
        this.settingsService.triggerOpenAdminSettingsModal();
        break;
      case 'enter-backoffice':
        this.accountAdminService.userBackOfficeBS.next(true);
        this.router.navigate(['/backoffice']);
        break;
      default:
        break;
    }
    this.menuService.closeMobile();
  }

  private openBugReport(): void {
    // Reflect the URL like the mega menu does, without triggering a navigation.
    window.history.replaceState({}, '', '/backoffice/bugreports');
    this.dialog.open(BugreportComponent, { width: '600px', disableClose: true });
  }

  private isTopLevelVisible(
    item: TopLevelNavItem,
    context: { backoffice: boolean; adminToggle: boolean; featureFlags: FeatureFlagState },
  ): boolean {
    // Same visibility rules as the desktop top-level rail (TopLevelSideMenuComponent.items),
    // except mobile-only sections (e.g. "My Triggerbee") are shown here and hidden on desktop.
    if (item.hidden) {
      return false;
    }

    // Admin-only sections appear only while the admin toggle is on.
    const hiddenForNonAdmin = item.adminOnly && !context.adminToggle;
    if (hiddenForNonAdmin) {
      return false;
    }

    // Swap the nav set by area: backoffice sections only show on /backoffice,
    // and app sections only show outside it.
    const belongsToCurrentArea = context.backoffice ? !!item.backofficeOnly : !item.backofficeOnly;
    if (!belongsToCurrentArea) {
      return false;
    }

    // Feature-flagged sections appear only when their flag is enabled.
    const hiddenByFeatureFlag = !!item.featureFlag && !context.featureFlags[item.featureFlag];
    if (hiddenByFeatureFlag) {
      return false;
    }

    return true;
  }

  private buildEntries(
    entries: readonly SubNavEntry[],
    path: string,
    groupState: Map<string, boolean>,
  ): MobileNavEntryVm[] {
    const result: MobileNavEntryVm[] = [];
    for (const entry of entries) {
      if (isSubNavSeparator(entry)) {
        result.push({ kind: 'separator' });
        continue;
      }
      if (isSubNavGroup(entry)) {
        // Admin-only groups (e.g. the mega menu's "Admin only" block) are hidden from non-admins.
        if (entry.requiresAdmin && !this.isAdmin) continue;
        result.push(this.buildGroup(entry, path, groupState));
        continue;
      }
      result.push(this.buildLeaf(entry, path));
    }
    return result;
  }

  private buildGroup(group: SubNavGroup, path: string, groupState: Map<string, boolean>): MobileNavGroupVm {
    const explicit = groupState.get(group.translationKey);
    const containsActive = group.items.some((item) => this.isItemActive(item, path));
    return {
      kind: 'group',
      translationKey: group.translationKey,
      dataTest: group.dataTest ?? null,
      adminOnly: !!group.adminOnly,
      expanded: explicit ?? (containsActive || (group.defaultExpanded ?? false)),
      items: group.items.map((item) => this.buildLeaf(item, path)),
    };
  }

  private buildLeaf(item: SubNavItem, path: string): MobileNavLeafVm {
    // Site-admin-only links render disabled for everyone else, mirroring the desktop mega menu.
    const siteAdminLocked = !!item.siteAdminOnly && !this.isSiteAdmin();
    let kind: MobileNavLeafVm['kind'];
    if (siteAdminLocked || item.disabled) {
      kind = 'disabled';
    } else if (item.externalUrl) {
      kind = 'external';
    } else if (item.action) {
      kind = 'action';
    } else {
      kind = 'link';
    }
    return {
      kind,
      translationKey: item.translationKey,
      url: item.url ?? null,
      externalUrl: item.externalUrl ?? null,
      queryParams: item.queryParams ? { ...item.queryParams } : null,
      actionType: item.action ?? null,
      dataTest: item.dataTest ?? null,
      betaTag: !!item.betaTag,
      adminOnly: !!item.adminOnly,
      showUpgradeTag: this.shouldShowUpgradeTag(item),
      // Hidden for non-site-admins; settings-group leaves are built in buildGroup and stay disabled.
      children: this.visibleChildren(item).map((child) => this.buildLeaf(child, path)),
      showChildren: this.itemChildrenVisible(item, path),
    };
  }

  private shouldShowUpgradeTag(item: SubNavItem): boolean {
    const key = item.upgradeTagAccessKey;
    if (!key) return false;
    return this.tierAccess()?.[key] === false;
  }

  private shouldShowUpgradeTagForTopLevel(item: TopLevelNavItem): boolean {
    const key = item.upgradeTagAccessKey;
    if (!key) return false;
    return this.tierAccess()?.[key] === false;
  }

  private isItemActive(item: SubNavItem, path: string): boolean {
    return !!item.url && (path === item.url || path.startsWith(item.url + '/'));
  }

  private visibleChildren(item: SubNavItem): readonly SubNavItem[] {
    return (item.children ?? []).filter((child) => !child.siteAdminOnly || this.isSiteAdmin());
  }

  private itemChildrenVisible(item: SubNavItem, path: string): boolean {
    if (!this.visibleChildren(item).length || !item.url) return false;
    return isSubNavItemExpanded(item, path);
  }
}
